import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    watchlistSemanticFrameSx,
    watchlistTableFrameSx,
} from '../styles/watchlistMsiStyles';
import { CountryFlagText } from './shared/securityDisplayPrimitives';
import {
    duplicateSecurityReasonText,
    isDuplicateSecurityRow,
} from '../utils/watchlistDuplicateSecurity';
import useWatchlistReviewIdentity from './watchlist_table/useWatchlistReviewIdentity';
import SkippedUploadRowsTable, {
    notConsideredText,
    skippedRowForSecurity,
} from './watchlist_table/SkippedUploadRows';

const formatQty = (val) => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'number') return val;
    const parsed = Number(val);
    return Number.isNaN(parsed) ? String(val) : parsed;
};

const toNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') return val;
    const parsed = Number(val);
    return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => (
    value == null ? '' : String(value).trim()
);

const normalizeIdentifierType = (value) => normalizeText(value).toLowerCase();

const isInternalIdentifierType = (value) => normalizeIdentifierType(value) === 'cognive_id';

const formatPublicIdentifierType = (value) => {
    const type = normalizeIdentifierType(value);
    if (isInternalIdentifierType(type)) return '';
    return type === 'bloomberg_ticker' ? 'Bloomberg ticker' : type.toUpperCase();
};

const getIgnoredIdentifierType = (item) => {
    const explicitType = normalizeText(item?.security_id_type || item?.id_type || item?.identifier_type);
    if (explicitType) return formatPublicIdentifierType(explicitType);
    return '';
};

const getIgnoredIdentifierValue = (item) => {
    const explicitType = normalizeText(item?.security_id_type || item?.id_type || item?.identifier_type);
    if (isInternalIdentifierType(explicitType)) {
        return '';
    }
    return normalizeText(item?.security_id_value || item?.id_value || item?.identifier_value);
};

const formatList = (values) => {
    if (!Array.isArray(values)) return '—';
    const cleaned = values.map((value) => normalizeText(value)).filter(Boolean);
    return cleaned.length ? cleaned.join(', ') : '—';
};

const WATCHLIST_CHANGE_FIELD_LABELS = {
    attributes_json: 'Watchlist fields',
    selected_country: 'Country',
    uploaded_country_hint: 'Requested country',
};

const WATCHLIST_EXCLUSION_REASON_LABELS = {
    country_hint_not_available: 'Country not available',
    country_not_available: 'Country not available',
};

const humanizeCode = (value) => {
    const normalized = normalizeText(value);
    if (!normalized) return '—';
    return normalized
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (character) => character.toUpperCase());
};

const formatWatchlistChangeFields = (values) => {
    if (!Array.isArray(values)) return '—';
    const labels = values
        .map((value) => WATCHLIST_CHANGE_FIELD_LABELS[normalizeText(value)] || humanizeCode(value))
        .filter((value) => value && value !== '—');
    return labels.length ? labels.join(', ') : '—';
};

const formatWatchlistExclusionReason = (value) => {
    const normalized = normalizeText(value).toLowerCase();
    return WATCHLIST_EXCLUSION_REASON_LABELS[normalized] || humanizeCode(value);
};

const PortfolioChangeSection = ({ title, rows, options = {} }) => {
    const identity = useWatchlistReviewIdentity();
    if (!rows?.length) return null;
    const { borderColor, headerColor } = options;
    const tone = headerColor === 'success.main'
        ? 'success'
        : headerColor === 'error.main'
            ? 'danger'
            : 'warning';
    return (
        <Box
            role="region"
            aria-label={`${title} portfolio positions`}
            sx={[
                {
                    p: 1.2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: borderColor || 'divider',
                    bgcolor: (theme) => (
                        theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : alpha(theme.palette.action.hover, 0.08)
                    ),
                },
                watchlistSemanticFrameSx(tone),
            ]}
        >
            <Typography variant="subtitle2" sx={{ color: headerColor || 'text.primary', mb: 0.5 }}>
                {title}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', columnGap: 1, rowGap: 0.5 }}>
                <Box sx={{ typography: 'caption', color: 'text.secondary' }}>{identity.header}</Box>
                <Typography variant="caption" color="text.secondary">
                    Original
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Current
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Change
                </Typography>
            </Box>
            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                {rows.map((row, idx) => {
                    const oldVal = toNumber(row.old_qty);
                    const newVal = toNumber(row.new_qty);
                    let delta = null;
                    if (oldVal === null && newVal !== null) {
                        delta = newVal;
                    } else if (newVal === null && oldVal !== null) {
                        delta = -oldVal;
                    } else if (oldVal !== null && newVal !== null) {
                        delta = newVal - oldVal;
                    }
                    const deltaColor =
                        delta === null ? 'text.primary' : delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.primary';
                    return (
                        <Box
                            key={`${title}-${row.ticker}-${idx}`}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
                                columnGap: 1,
                                px: 0.6,
                                py: 0.35,
                                borderRadius: 0.75,
                                bgcolor: (theme) => {
                                    if (idx % 2 !== 1 || theme.palette.highContrast) {
                                        return 'transparent';
                                    }
                                    return theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.04)
                                        : alpha(theme.palette.action.hover, 0.12);
                                },
                            }}
                        >
                            {identity.renderIdentity(row)}
                            <Typography variant="body2">{formatQty(row.old_qty)}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatQty(row.new_qty)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: deltaColor }}>
                                {delta === null ? '—' : formatQty(delta)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            {identity.selector}
        </Box>
    );
};

const formatCellText = (value) => {
    const text = normalizeText(value);
    return text || '—';
};

const UNASSIGNED_FIELD_LABEL = 'Unassigned';

const isUnassignedWatchlistMetadataValue = (value) => {
    const text = normalizeText(value).toLowerCase();
    return !text || text === 'unknown';
};

const formatWatchlistAttributeCellText = (value) => (
    isUnassignedWatchlistMetadataValue(value)
        ? UNASSIGNED_FIELD_LABEL
        : formatCellText(value)
);

const FACTOR_COVERAGE_NOTES = {
    not_covered: 'Not covered by current factor data. Apply leaves it out and lists it in Security Resolution.',
    imputed: 'Verified factor data is unavailable. Securities without verified data are excluded.',
};

const getWatchlistAddRows = (rows) => (
    Array.isArray(rows) ? rows : Object.values(rows || {})
);

const slotLabel = (name) => {
    const match = normalizeText(name).match(/^attribute([1-5])$/i);
    return match ? `Attribute ${match[1]}` : normalizeText(name);
};

const getRevisionDetailRows = (bucket, side = 'after') => (
    getWatchlistAddRows(bucket)
        .map((item) => item?.[side] || item)
        .filter(Boolean)
);

const getRevisionChangedRows = (details) => (
    [
        ...getWatchlistAddRows(details.identifier_changed),
        ...getWatchlistAddRows(details.country_changed),
        ...getWatchlistAddRows(details.attributes_changed),
    ]
        .map((item) => ({
            ...(item?.after || item),
            __before: item?.before || null,
            changed_fields: item?.changed_fields || [],
        }))
        .filter(Boolean)
);

const watchlistRowIdentifier = (row) => ({
    type: formatPublicIdentifierType(
        row?.inbound_id_type || row?.uploaded_identifier_type,
    ),
    value: normalizeText(
        row?.inbound_id_value
        || row?.uploaded_identifier_value
        || row?.uploaded_ticker
        || row?.ticker,
    ),
});

// A row's attribute values by name. The server keeps security inputs out of
// them, so every name is an attribute and shows like any other.
const revisionRowAttributes = (row) => {
    const attrs = row?.attributes_json || {};
    return attrs && typeof attrs === 'object' ? attrs : {};
};

const watchlistAttributeValue = (row, name) => revisionRowAttributes(row)?.[name];

const hasWatchlistAttributeValue = (row, name) => (
    !isUnassignedWatchlistMetadataValue(watchlistAttributeValue(row, name))
);

const watchlistAttributeNames = (rows) => {
    const attributeNames = [];
    const addAttribute = (name) => {
        if (!attributeNames.includes(name)) attributeNames.push(name);
    };
    rows.forEach((row) => {
        const before = row?.__before;
        [row, before].filter(Boolean).forEach((candidate) => {
            Object.keys(revisionRowAttributes(candidate)).forEach((name) => {
                if (
                    hasWatchlistAttributeValue(row, name)
                    || hasWatchlistAttributeValue(before, name)
                ) {
                    addAttribute(name);
                }
            });
        });
    });
    return attributeNames;
};

const watchlistSectionFrameSx = (borderColor, tone) => [
    {
        p: 1.2,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: borderColor || 'divider',
        bgcolor: (theme) => (
            theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : alpha(theme.palette.action.hover, 0.08)
        ),
    },
    watchlistSemanticFrameSx(tone),
];

export const WatchlistChangeSection = ({ title, rows, options = {} }) => {
    const identity = useWatchlistReviewIdentity();
    if (!rows?.length) return null;
    const {
        borderColor,
        headerColor,
        showReason = false,
        showChange = false,
        showTitle = true,
        rowNote = () => null,
        tableAriaLabel = `Watchlist upload ${title.toLowerCase()}`,
    } = options;
    const attributeNames = watchlistAttributeNames(rows);
    const tone = headerColor === 'success.main'
        ? 'success'
        : headerColor === 'error.main'
            ? 'danger'
            : 'warning';
    return (
        <Box sx={watchlistSectionFrameSx(borderColor, tone)}>
            {showTitle && <Typography variant="subtitle2" sx={{ color: headerColor || 'text.primary', mb: 0.5 }}>
                {title}
            </Typography>}
            <TableContainer component={Paper} variant="outlined" sx={[{ mt: 1, borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label={tableAriaLabel}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>{identity.header}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                            {showChange && (
                                <TableCell sx={{ fontWeight: 700 }}>Change</TableCell>
                            )}
                            {showReason && (
                                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                            )}
                            {attributeNames.map((name) => (
                                <TableCell key={name} sx={{ fontWeight: 700 }}>
                                    {slotLabel(name)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => {
                            const identifier = watchlistRowIdentifier(row);
                            const rowKey = `${identifier.type || 'identifier'}-${identifier.value || row?.cognive_id || index}-${index}`;
                            const note = rowNote(row);
                            return (
                                <TableRow key={rowKey}>
                                    <TableCell>
                                        {identity.renderIdentity(row)}
                                        {note && (
                                            <Typography
                                                variant="caption"
                                                color="warning.main"
                                                display="block"
                                                sx={{ mt: 0.5 }}
                                            >
                                                {note}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell><CountryFlagText country={row?.selected_country || row?.uploaded_country_hint || row?.country} /></TableCell>
                                    {showChange && (
                                        <TableCell>{formatWatchlistChangeFields(row?.changed_fields)}</TableCell>
                                    )}
                                    {showReason && (
                                        <TableCell>
                                            {isDuplicateSecurityRow(row)
                                                ? duplicateSecurityReasonText(row)
                                                : formatWatchlistExclusionReason(
                                                    row?.resolution_reason_code || row?.exclusion_category,
                                                )}
                                        </TableCell>
                                    )}
                                    {attributeNames.map((name) => (
                                        <TableCell key={name}>
                                            {formatWatchlistAttributeCellText(
                                                watchlistAttributeValue(row, name),
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {identity.selector}
        </Box>
    );
};

const IgnoredSection = ({ rows }) => {
    const identity = useWatchlistReviewIdentity();
    if (!rows?.length) return null;
    return (
        <Box
            sx={[
                {
                    p: 1.2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'warning.light',
                    bgcolor: (theme) => (
                        theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : alpha(theme.palette.action.hover, 0.08)
                    ),
                },
                watchlistSemanticFrameSx('warning'),
            ]}
        >
            <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 0.5 }}>
                Ignored
            </Typography>
            <Typography variant="caption" color="text.secondary">
                Upload a watchlist that includes these securities and re-sync to include them.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={[{ mt: 1, borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label="Ignored securities not found in watchlist">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>{identity.header}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((item, index) => {
                            const identifierType = getIgnoredIdentifierType(item);
                            const identifierValue = getIgnoredIdentifierValue(item);
                            const ticker = normalizeText(item?.ticker);
                            const rowKey = `${identifierType || 'identifier'}-${identifierValue || ticker || index}-${index}`;
                            return (
                                <TableRow key={rowKey}>
                                    <TableCell>
                                        {identity.renderIdentity(item)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {identity.selector}
        </Box>
    );
};

const ExpansionWarningSection = ({ rows }) => {
    const identity = useWatchlistReviewIdentity();
    if (!rows?.length) return null;
    return (
        <Box
            sx={[
                {
                    p: 1.2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'warning.light',
                    bgcolor: (theme) => (
                        theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : alpha(theme.palette.action.hover, 0.08)
                    ),
                },
                watchlistSemanticFrameSx('warning'),
            ]}
        >
            <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 0.5 }}>
                Multi-listing matches
            </Typography>
            <Typography variant="caption" color="text.secondary">
                Uploaded quantities were copied to each matching watchlist listing.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={[{ mt: 1, borderRadius: 1.5 }, watchlistTableFrameSx]}>
                <Table size="small" aria-label="Portfolio identifier expansion warnings">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>{identity.header}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Quantity per Listing</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Total Quantity</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Matched Countries</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Matched Tickers</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((item, index) => {
                            const identifierType = formatPublicIdentifierType(item?.identifier_type);
                            const identifierValue = isInternalIdentifierType(item?.identifier_type)
                                ? ''
                                : normalizeText(item?.identifier_value);
                            const rowKey = `${identifierType || 'identifier'}-${identifierValue || index}-${index}`;
                            return (
                                <TableRow key={rowKey}>
                                    <TableCell>
                                        {identity.renderIdentity(item)}
                                    </TableCell>
                                    <TableCell>{normalizeText(item?.uploaded_quantity) || '—'}</TableCell>
                                    <TableCell>{normalizeText(item?.total_quantity) || '—'}</TableCell>
                                    <TableCell>{formatList(item?.matched_countries)}</TableCell>
                                    <TableCell>{formatList(item?.matched_tickers)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {identity.selector}
        </Box>
    );
};

export default function ImportDiffDetails({
    diffDetails = {},
    diffSummary = {},
    ignoredWatchlist = [],
    showMissingSedols = true,
    missingSedolsLabel = 'Skipped securities with missing prices or not in security master:',
    emptyMessage = 'No changes detected.',
    visibleSections = null,
    mode = 'portfolio',
    notConsidered = { rows: [], total: 0, linkedExcel: false },
}) {
    const safeDetails = diffDetails || {};
    const safeSummary = diffSummary || {};
    const sectionVisible = (key) => (
        !Array.isArray(visibleSections) || visibleSections.includes(key)
    );
    const watchlistAddMode = mode === 'watchlist_add';
    if (watchlistAddMode) {
        const rawAddedRows = getWatchlistAddRows(safeDetails.added);
        return (
            <Box display="flex" flexDirection="column" gap={1.5}>
                {(!sectionVisible('added') || rawAddedRows.length === 0) && (
                    <Typography variant="body2">
                        {emptyMessage}
                    </Typography>
                )}
                {sectionVisible('added') && <WatchlistChangeSection title="Added" rows={rawAddedRows} options={{
                    borderColor: 'success.light',
                    headerColor: 'success.main',
                    tableAriaLabel: 'Watchlist additions',
                }} />}
            </Box>
        );
    }
    const watchlistUploadMode = mode === 'watchlist_upload';
    if (watchlistUploadMode) {
        const expectedPreview = (
            safeSummary.provisional === true
            && String(safeSummary.status || '') === 'pending_review'
        );
        const uploadSectionTitle = (label) => (
            expectedPreview ? `Expected ${label.toLowerCase()}` : label
        );
        const uploadAddedRows = [
            ...getRevisionDetailRows(safeDetails.added, 'after'),
            ...getRevisionDetailRows(safeDetails.restored_from_history, 'after'),
        ];
        const uploadRemovedRows = getRevisionDetailRows(safeDetails.removed, 'before');
        const uploadChangedRows = getRevisionChangedRows(safeDetails);
        const uploadExcludedRows = [
            ...getRevisionDetailRows(safeDetails.unresolved_excluded, 'after'),
            ...getRevisionDetailRows(safeDetails.inactive_excluded, 'after'),
        ];
        const hasUploadSections = (
            (sectionVisible('added') && uploadAddedRows.length)
            || (sectionVisible('removed') && uploadRemovedRows.length)
            || (sectionVisible('changed') && uploadChangedRows.length)
            || (sectionVisible('ignored') && uploadExcludedRows.length)
        );
        return (
            <Box display="flex" flexDirection="column" gap={1.5}>
                {!hasUploadSections && (
                    <Typography variant="body2">
                        {emptyMessage}
                    </Typography>
                )}
                {sectionVisible('added') && <WatchlistChangeSection title={uploadSectionTitle('Added')} rows={uploadAddedRows} options={{
                    borderColor: 'success.light',
                    headerColor: 'success.main',
                    // The published Watchlist holds it without factor data, so
                    // it is not an ordinary addition.
                    rowNote: (row) => FACTOR_COVERAGE_NOTES[row?.factor_coverage] || null,
                }} />}
                {sectionVisible('removed') && <WatchlistChangeSection title={uploadSectionTitle('Removed')} rows={uploadRemovedRows} options={{
                    borderColor: 'error.light',
                    headerColor: 'error.main',
                    // A held security whose file row was not considered reads as removed.
                    rowNote: (row) => {
                        const skipped = skippedRowForSecurity(notConsidered.rows, row);
                        if (!skipped) return null;
                        const fileRow = skipped.rowNumber == null ? 'its row' : `row ${skipped.rowNumber}`;
                        return `Removed because ${fileRow} was not considered: ${skipped.problem}`;
                    },
                }} />}
                {sectionVisible('changed') && <WatchlistChangeSection title={uploadSectionTitle('Changed')} rows={uploadChangedRows} options={{
                    borderColor: 'warning.light',
                    headerColor: 'warning.main',
                    showChange: true,
                }} />}
                {sectionVisible('ignored') && <WatchlistChangeSection title="Excluded" rows={uploadExcludedRows} options={{
                    borderColor: 'warning.light',
                    headerColor: 'warning.main',
                    showReason: true,
                }} />}
                {notConsidered.rows.length > 0 && (
                    <Box sx={watchlistSectionFrameSx('warning.light', 'warning')}>
                        <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 0.5 }}>
                            {`Not considered (${notConsidered.total})`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {notConsideredText({
                                linkedExcel: notConsidered.linkedExcel,
                                shown: notConsidered.rows.length,
                                total: notConsidered.total,
                            })}
                        </Typography>
                        <SkippedUploadRowsTable rows={notConsidered.rows} />
                    </Box>
                )}
            </Box>
        );
    }
    const addedRows = (safeDetails.added || []).map((item) => ({
        ...item,
        old_qty: null,
        new_qty: item.new_qty,
    }));
    const removedRows = (safeDetails.removed || []).map((item) => ({
        ...item,
        old_qty: item.old_qty,
        new_qty: null,
    }));
    const changedRows = (safeDetails.changed || []).map((item) => ({
        ...item,
        old_qty: item.old_qty,
        new_qty: item.new_qty,
    }));
    const hasChanges = (
        (sectionVisible('added') && addedRows.length)
        || (sectionVisible('removed') && removedRows.length)
        || (sectionVisible('changed') && changedRows.length)
    );
    const ignoredRows = ignoredWatchlist || [];
    const expansionWarnings = safeSummary.warnings?.watchlist_identifier_expansions || [];
    const hasAnySection = hasChanges || ignoredRows.length || expansionWarnings.length;
    const missingSedols = safeSummary.missing_sedols || [];
    const nullQuantities = safeSummary.null_quantities || [];

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            {!hasAnySection && (
                <Typography variant="body2">
                    {emptyMessage}
                </Typography>
            )}
            {sectionVisible('added') && <PortfolioChangeSection title="Added" rows={addedRows} options={{
                borderColor: 'success.light',
                headerColor: 'success.main',
            }} />}
            {sectionVisible('removed') && <PortfolioChangeSection title="Removed" rows={removedRows} options={{
                borderColor: 'error.light',
                headerColor: 'error.main',
            }} />}
            {sectionVisible('changed') && <PortfolioChangeSection title="Changed" rows={changedRows} options={{
                borderColor: 'warning.light',
                headerColor: 'warning.main',
            }} />}
            <ExpansionWarningSection rows={expansionWarnings} />
            <IgnoredSection rows={ignoredRows} />
            {showMissingSedols && missingSedols.length > 0 && (
                <Typography variant="caption" color="warning.main" display="block">
                    {missingSedolsLabel} {missingSedols.length}
                </Typography>
            )}
            {nullQuantities.length > 0 && (
                <Typography variant="caption" color="warning.main" display="block">
                    Quantities were missing and inferred as 0 for: {nullQuantities.join(', ')}
                </Typography>
            )}
        </Box>
    );
}

ImportDiffDetails.propTypes = {
    diffDetails: PropTypes.object,
    diffSummary: PropTypes.object,
    ignoredWatchlist: PropTypes.array,
    showMissingSedols: PropTypes.bool,
    missingSedolsLabel: PropTypes.string,
    emptyMessage: PropTypes.string,
    visibleSections: PropTypes.arrayOf(PropTypes.string),
    mode: PropTypes.oneOf(['portfolio', 'watchlist_add', 'watchlist_upload']),
    notConsidered: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.object).isRequired,
        total: PropTypes.number.isRequired,
        linkedExcel: PropTypes.bool,
    }),
};
